import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  BanknotesIcon,
  CurrencyPoundIcon,
  ReceiptRefundIcon,
  TrophyIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import ReportLayout, { ReportFilterSelect } from "./ReportLayout";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  MockPartSale,
  MockPerson,
  SALESPEOPLE,
  SALES_CHANNELS,
  buildPartSales,
  monthsAgo,
  today,
} from "./reportsMockData";

interface SalespersonRollup {
  person: MockPerson;
  sales: MockPartSale[];
  units: number;
  revenue: number;
  profit: number;
  topChannel: string | null;
  topCategory: string | null;
  lastSaleDate: string | null;
}

const fmtInt = (n: number) => n.toLocaleString("en-GB");

/**
 * Salesman Report — one row per salesperson, with all the part sales they
 * made (and an expandable per-salesperson drilldown).
 */
export default function SalesmanReport() {
  const { setInfo } = usePageNameContext();
  const [dateRange, setDateRange] = useState({
    from: monthsAgo(3),
    to: today(),
  });
  const [channel, setChannel] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setInfo({
      name: "Salesman Report",
      desc: "Performance by salesperson — units, revenue, profit and top categories",
    });
  }, [setInfo]);

  const allSales = useMemo(
    () => buildPartSales(`${dateRange.from}T00:00:00Z`, `${dateRange.to}T23:59:59Z`),
    [dateRange.from, dateRange.to]
  );

  const filtered = useMemo(
    () =>
      channel === "all"
        ? allSales
        : allSales.filter((s) => s.salesChannel === channel),
    [allSales, channel]
  );

  const rollups: SalespersonRollup[] = useMemo(() => {
    const byId = new Map<string, SalespersonRollup>();
    for (const p of SALESPEOPLE) {
      byId.set(p.id, {
        person: p,
        sales: [],
        units: 0,
        revenue: 0,
        profit: 0,
        topChannel: null,
        topCategory: null,
        lastSaleDate: null,
      });
    }

    for (const s of filtered) {
      const row = byId.get(s.salespersonId);
      if (!row) continue;
      row.sales.push(s);
      row.units += s.quantity;
      row.revenue += s.total;
      row.profit += s.profit;
      if (!row.lastSaleDate || s.saleDate > row.lastSaleDate) {
        row.lastSaleDate = s.saleDate;
      }
    }

    for (const row of byId.values()) {
      if (!row.sales.length) continue;
      const cat = new Map<string, number>();
      const ch = new Map<string, number>();
      for (const s of row.sales) {
        cat.set(s.partCategory, (cat.get(s.partCategory) ?? 0) + s.total);
        ch.set(s.salesChannel, (ch.get(s.salesChannel) ?? 0) + s.total);
      }
      row.topCategory = [...cat.entries()].sort((a, b) => b[1] - a[1])[0][0];
      row.topChannel = [...ch.entries()].sort((a, b) => b[1] - a[1])[0][0];
    }

    return [...byId.values()].sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);

  const totals = useMemo(
    () => ({
      lines: filtered.length,
      units: filtered.reduce((s, r) => s + r.quantity, 0),
      revenue: filtered.reduce((s, r) => s + r.total, 0),
      profit: filtered.reduce((s, r) => s + r.profit, 0),
    }),
    [filtered]
  );

  const topPerformer = rollups[0];
  const margin =
    totals.revenue === 0
      ? 0
      : Math.round((totals.profit / totals.revenue) * 100);

  const maxRevenue = rollups.reduce((m, r) => Math.max(m, r.revenue), 0) || 1;

  const csvRows = useMemo(
    () =>
      rollups.map((r) => ({
        salesperson: r.person.name,
        role: r.person.role,
        lines: r.sales.length,
        units: r.units,
        revenueGBP: r.revenue,
        profitGBP: r.profit,
        marginPercent:
          r.revenue === 0 ? 0 : Math.round((r.profit / r.revenue) * 100),
        topCategory: r.topCategory ?? "",
        topChannel: r.topChannel ?? "",
        lastSaleDate: r.lastSaleDate ? r.lastSaleDate.slice(0, 10) : "",
      })),
    [rollups]
  );

  return (
    <ReportLayout
      title="Salesman Report"
      description="Who sold what in the selected window — ranked by revenue and drillable to individual sales."
      icon={UserGroupIcon}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      controls={
        <ReportFilterSelect
          label="Channel"
          value={channel}
          onChange={setChannel}
          options={[
            { value: "all", label: "All channels" },
            ...SALES_CHANNELS.map((c) => ({ value: c, label: c })),
          ]}
          width="9rem"
        />
      }
      csvRows={csvRows}
      csvFilename="salesman-report"
      headlineStats={
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <HeadlineStat
            tone="blue"
            icon={UserGroupIcon}
            value={fmtInt(rollups.filter((r) => r.sales.length > 0).length)}
            label="Active salespeople"
            sublabel={`of ${fmtInt(SALESPEOPLE.length)}`}
          />
          <HeadlineStat
            tone="cyan"
            icon={ReceiptRefundIcon}
            value={fmtInt(totals.lines)}
            label="Order lines"
            sublabel={`${fmtInt(totals.units)} units`}
          />
          <HeadlineStat
            tone="emerald"
            icon={CurrencyPoundIcon}
            value={PriceFormat(totals.revenue)}
            label="Revenue"
            sublabel={`${fmtInt(margin)}% margin`}
          />
          <HeadlineStat
            tone="violet"
            icon={BanknotesIcon}
            value={PriceFormat(totals.profit)}
            label="Profit"
            sublabel="After unit cost"
          />
          <HeadlineStat
            tone="amber"
            icon={TrophyIcon}
            value={topPerformer?.person.name ?? "—"}
            label="Top performer"
            sublabel={
              topPerformer
                ? `${PriceFormat(topPerformer.revenue)} revenue`
                : undefined
            }
          />
        </div>
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th className="w-6"></Th>
                <Th>Salesperson</Th>
                <Th className="text-right">Lines</Th>
                <Th className="text-right">Units</Th>
                <Th className="text-right">Revenue</Th>
                <Th>Share of revenue</Th>
                <Th className="text-right">Profit</Th>
                <Th className="text-right">Margin</Th>
                <Th>Top category</Th>
                <Th>Top channel</Th>
                <Th>Last sale</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rollups.map((r, index) => {
                const isOpen = expandedId === r.person.id;
                const rowMargin =
                  r.revenue === 0
                    ? 0
                    : Math.round((r.profit / r.revenue) * 100);
                const sharePct = Math.round((r.revenue / maxRevenue) * 100);
                return (
                  <FragmentRow key={r.person.id}>
                    <tr
                      className={classNames(
                        "cursor-pointer transition-colors hover:bg-primary-50/40",
                        isOpen ? "bg-primary-50/60" : ""
                      )}
                      onClick={() =>
                        setExpandedId(isOpen ? null : r.person.id)
                      }
                    >
                      <td className="pl-4 pr-1 py-2 align-middle">
                        <span
                          className={classNames(
                            "inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                            index === 0
                              ? "bg-amber-100 text-amber-700"
                              : index === 1
                                ? "bg-slate-200 text-slate-700"
                                : index === 2
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-slate-100 text-slate-500"
                          )}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-700">
                            {r.person.initials}
                          </span>
                          <div className="flex flex-col leading-tight">
                            <span className="font-semibold text-slate-900">
                              {r.person.name}
                            </span>
                            <span className="text-[11px] text-slate-500">
                              {r.person.role}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {fmtInt(r.sales.length)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {fmtInt(r.units)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                        {PriceFormat(r.revenue)}
                      </td>
                      <td className="min-w-[10rem] px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
                              style={{
                                width: `${Math.max(
                                  sharePct,
                                  r.revenue > 0 ? 3 : 0
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="w-10 flex-none text-right text-[11px] tabular-nums text-slate-500">
                            {sharePct}%
                          </span>
                        </div>
                      </td>
                      <td
                        className={classNames(
                          "px-3 py-2 text-right font-semibold tabular-nums",
                          r.profit >= 0 ? "text-emerald-700" : "text-rose-600"
                        )}
                      >
                        {PriceFormat(r.profit)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {fmtInt(rowMargin)}%
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {r.topCategory ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {r.topChannel ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {r.lastSaleDate
                          ? formatDateTime(r.lastSaleDate, DateTime.DATE_MED)
                          : "—"}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={11} className="px-4 pb-4 pt-2">
                          <ExpandedSales sales={r.sales} />
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}
            </tbody>
            {rollups.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr className="text-sm font-semibold text-slate-700">
                  <td />
                  <td className="px-3 py-2">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.lines)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.units)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.revenue)}
                  </td>
                  <td />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.profit)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(margin)}%
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </ReportLayout>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

function ExpandedSales({ sales }: { sales: MockPartSale[] }) {
  if (!sales.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-xs text-slate-500">
        No sales recorded for this salesperson in the selected window.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-xs">
        <thead className="bg-slate-50">
          <tr>
            <Th className="!text-[10px]">Sale date</Th>
            <Th className="!text-[10px]">Order</Th>
            <Th className="!text-[10px]">Part</Th>
            <Th className="!text-[10px]">Donor</Th>
            <Th className="!text-[10px]">Channel</Th>
            <Th className="!text-[10px] !text-right">Qty</Th>
            <Th className="!text-[10px] !text-right">Total</Th>
            <Th className="!text-[10px] !text-right">Profit</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sales.slice(0, 30).map((s) => (
            <tr key={s.id} className="hover:bg-slate-50/60">
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-600">
                {formatDateTime(s.saleDate, DateTime.DATE_MED)}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px] text-slate-500">
                {s.orderNumber}
              </td>
              <td className="px-3 py-1.5 text-slate-700">{s.partName}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
                {s.vehicle.registration}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
                {s.salesChannel}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-700">
                {s.quantity}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">
                {PriceFormat(s.total)}
              </td>
              <td
                className={classNames(
                  "whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums",
                  s.profit >= 0 ? "text-emerald-700" : "text-rose-600"
                )}
              >
                {PriceFormat(s.profit)}
              </td>
            </tr>
          ))}
        </tbody>
        {sales.length > 30 && (
          <tfoot>
            <tr>
              <td
                colSpan={8}
                className="bg-slate-50 px-3 py-1.5 text-[11px] italic text-slate-500"
              >
                Showing 30 of {sales.length} sales. Use the CSV export to see
                the full list.
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
